const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

async function fetchAPI(url: string, options: RequestInit = {}): Promise<any> {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(url, { ...options, headers });

  if (response.status === 204) {
    return null;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData['hydra:description'] || errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export interface Badge {
  id: number;
  name: string;
  description?: string;
  icon?: string;
  type: 'course_completion' | 'milestone' | 'streak';
  isActive: boolean;
}

export interface UserBadge {
  id: number;
  badge: Badge;
  awardedAt: string;
}

export interface BadgeProgress {
  badgeId: number;
  name: string;
  description?: string;
  icon?: string;
  type: string;
  isEarned: boolean;
  progress: number | null;
  target: number | null;
}

export interface UserStreak {
  currentStreak: number;
  longestStreak: number;
  lastActivityDate?: string;
  isAtRisk: boolean;
  rank: number;
}

export interface LeaderboardEntry {
  rank: number;
  userId: number;
  name: string;
  value: number;
}

export interface Leaderboard {
  type: 'courses' | 'badges' | 'streak';
  entries: LeaderboardEntry[];
}

export interface MyPosition {
  userId: number;
  completedCourses: number;
  courseRank: number;
  badges: number;
  badgeRank: number;
  currentStreak: number;
  longestStreak: number;
  streakRank: number;
  isAtRisk: boolean;
}

class GamificationService {
  /**
   * Get current user's badges
   */
  async getMyBadges(): Promise<UserBadge[]> {
    return fetchAPI(`${API_BASE_URL}/api/gamification/my-badges`);
  }

  /**
   * Get badge progress for current user
   */
  async getBadgeProgress(): Promise<BadgeProgress[]> {
    return fetchAPI(`${API_BASE_URL}/api/gamification/badge-progress`);
  }

  /**
   * Get current user's streak info
   */
  async getMyStreak(): Promise<UserStreak> {
    return fetchAPI(`${API_BASE_URL}/api/gamification/my-streak`);
  }

  /**
   * Get leaderboard data
   */
  async getLeaderboard(type: 'courses' | 'badges' | 'streak' = 'courses', limit: number = 10): Promise<Leaderboard> {
    return fetchAPI(`${API_BASE_URL}/api/leaderboard?type=${type}&limit=${limit}`);
  }

  /**
   * Get current user's position on leaderboard
   */
  async getMyPosition(): Promise<MyPosition> {
    return fetchAPI(`${API_BASE_URL}/api/leaderboard/me`);
  }

  /**
   * Get all available badges
   */
  async getAvailableBadges(): Promise<Badge[]> {
    const response = await fetchAPI(`${API_BASE_URL}/api/badges?isActive=true`);
    return response['hydra:member'] || response['member'] || [];
  }
}

export const gamificationService = new GamificationService();
export default gamificationService;
